import { CUSTOMER_REQUEST_TYPES } from "@routebot/constants";

// Mirrors backend/modules/customer-portal/extra-service.js.
// The date is a calendar day. The other fields accept any non-empty text.

export const EXTRA_SERVICE_TYPE = "extra_service";

export const EXTRA_SERVICE_LIMITS = {
  place: 200,
  time: 80,
  headcount: 200,
};

const DATE_YEAR_MIN = 2000;
const DATE_YEAR_MAX = 2100;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const FIELD_ORDER = ["date", "origin", "destination", "departureTime", "returnTime", "headcount"];

const FIELD_LIMITS = {
  origin: EXTRA_SERVICE_LIMITS.place,
  destination: EXTRA_SERVICE_LIMITS.place,
  departureTime: EXTRA_SERVICE_LIMITS.time,
  returnTime: EXTRA_SERVICE_LIMITS.time,
  headcount: EXTRA_SERVICE_LIMITS.headcount,
};

export const requestTypeOptions = () => {
  const rest = Object.values(CUSTOMER_REQUEST_TYPES || {}).filter((value) => value !== EXTRA_SERVICE_TYPE);
  return [EXTRA_SERVICE_TYPE, ...rest];
};

export const parseExtraServiceDate = (value) => {
  if (typeof value !== "string") return null;
  const match = DATE_PATTERN.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < DATE_YEAR_MIN || year > DATE_YEAR_MAX) return null;
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (utc.getUTCFullYear() !== year || utc.getUTCMonth() !== month - 1 || utc.getUTCDate() !== day) {
    return null;
  }
  return `${match[1]}-${match[2]}-${match[3]}`;
};

export const formatExtraServiceDate = (value) => {
  const iso = parseExtraServiceDate(typeof value === "string" ? value : "");
  if (!iso) return "";
  const [year, month, day] = iso.split("-");
  return `${day}.${month}.${year}`;
};

const isZeroWidth = (code) => code === 0x200b || code === 0x200c || code === 0x200d || code === 0xfeff;

const cleanFreeText = (value, max) => {
  if (value == null || value === "") return { ok: false, reason: "required" };
  if (typeof value !== "string") return { ok: false, reason: "invalid" };
  let text = "";
  let breakAsSpace = false;
  for (const ch of value) {
    const code = ch.codePointAt(0);
    if (isZeroWidth(code)) continue;
    if (code === 0x09 || code === 0x0a || code === 0x0d) {
      breakAsSpace = true;
      continue;
    }
    if (code <= 0x1f || code === 0x7f) continue;
    if (breakAsSpace) {
      text += " ";
      breakAsSpace = false;
    }
    text += ch;
  }
  text = text.trim();
  if (!text) return { ok: false, reason: "required" };
  if (text.length > max) return { ok: false, reason: "too_long" };
  return { ok: true, value: text };
};

export const normalizeExtraService = (input) => {
  const source = input && typeof input === "object" ? input : {};
  const value = {};
  for (const field of FIELD_ORDER) {
    if (field === "date") {
      if (source.date == null || source.date === "") return { ok: false, field, reason: "required" };
      if (typeof source.date !== "string") return { ok: false, field, reason: "invalid" };
      const iso = parseExtraServiceDate(source.date);
      if (!iso) return { ok: false, field, reason: "invalid" };
      value.date = iso;
      continue;
    }
    const cleaned = cleanFreeText(source[field], FIELD_LIMITS[field]);
    if (!cleaned.ok) return { ok: false, field, reason: cleaned.reason };
    value[field] = cleaned.value;
  }
  return { ok: true, value };
};

export const extraServiceErrorKey = (result) => {
  if (!result || result.ok) return "";
  if (result.field === "date" && result.reason === "invalid") return "customerPortal.tripDateInvalid";
  if (result.reason === "too_long") return "customerPortal.tripTooLong";
  if (result.field === "date") return "customerPortal.tripDateRequired";
  if (result.field === "origin") return "customerPortal.tripOriginRequired";
  if (result.field === "destination") return "customerPortal.tripDestinationRequired";
  if (result.field === "departureTime") return "customerPortal.tripDepartureRequired";
  if (result.field === "returnTime") return "customerPortal.tripReturnRequired";
  if (result.field === "headcount") return "customerPortal.tripHeadcountRequired";
  return "customerPortal.saveFailed";
};

export const extraServiceFieldLabelKey = (field) => {
  if (field === "origin") return "customerPortal.tripOrigin";
  if (field === "destination") return "customerPortal.tripDestination";
  if (field === "departureTime") return "customerPortal.tripDeparture";
  if (field === "returnTime") return "customerPortal.tripReturn";
  if (field === "headcount") return "customerPortal.tripHeadcount";
  return "customerPortal.date";
};
